import { useEffect, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { IWizardCommonProps, Wizard, WizardPage, wizardServices } from "cayo.ui.wizards";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { addAppAzureSubscription, addAzureSubscription } from "./api";
import { grantConsentModern, validatePrincipalAccountName } from "./logic";
import { messages } from "./messages";
import { InitialState, wizardStateReducer } from "./reducer";
import { AppCertForm } from "./steps/AppCertForm";
import { CreateApplication } from "./steps/CreateApplication";
import { Finish } from "./steps/Finish";
import { SelectFlow } from "./steps/SelectFlow";
import { SelectSubscription } from "./steps/SelectSubscription";
import { IAuthResponse } from "./types";

export const AzureSubscriptionWizard = (props: IWizardCommonProps) => {
  const intl = useIntl();
  const azureSubscriptionService = wizardServices.azureSubscriptionService;

  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, InitialState);
  const stateRef = useRef(InitialState);
  const wizardRef = useRef<{ resetError: () => void }>(null);
  const [loadingText, setLoadingText] = useState<undefined | string>(undefined);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const { grantUrl } = wizardState;
  const [consentCheck, setConsentCheck] = useState<{
    state: string;
    isChecking?: boolean;
  }>({
    state: intl.formatMessage(messages.unknown),
  });

  const principalLimitError = validatePrincipalAccountName(
    wizardState?.principalAccountName!,
    intl.formatMessage(messages.principalAccountError)
  );

  stateRef.current = wizardState;

  const exchangeAuthCode = async (auth: IAuthResponse) => {
    if (!auth.code) {
      dispatchWizardState({
        type: "update",
        payload: { consentError: auth.error_description },
      });

      return;
    }

    setConsentCheck({
      state: "",
      isChecking: true,
    });

    try {
      const refreshToken = await azureSubscriptionService.exchangeAuthCode(
        auth.code!,
        wizardState.configurationAccount!
      );

      setConsentCheck({
        state: "granted",
        isChecking: false,
      });

      dispatchWizardState({
        type: "update",
        payload: { refreshToken, createError: "" },
      });
    } catch (e) {
      setConsentCheck({
        state: "",
        isChecking: false,
      });
      dispatchWizardState({
        type: "update",
        payload: { consentError: e },
      });
    } finally {
      setLoadingText(undefined);
    }
  };

  const grant = (account: string) => {
    return azureSubscriptionService
      .grant(account!, endpoints.redirectUri)
      .then((response) => {
        dispatchWizardState({
          type: "update",
          payload: { grantUrl: response.url, consentError: "" },
        });
      })
      .catch((e) => {
        setLoadingText(undefined);

        dispatchWizardState({
          type: "update",
          payload: { azureError: e },
        });

        throw e;
      })
      .then(() => setLoadingText(undefined));
  };

  useEffect(() => {
    grant("");
  }, []);

  const isAppCertFlow = wizardState.selectedFlow === "applicationCertificate";

  return (
    <Wizard
      loadingText={loadingText}
      title={intl.formatMessage(messages.mainTitle)}
      isFinished={isFinished}
      activePage={0}
      ref={wizardRef}
      closeConfirmationMessage={intl.formatMessage(messages.closeConfirmation)}
      showStepError
      {...props}
    >
      <WizardPage
        isInputValid={!!wizardState.selectedFlow}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(messages.selectFlowTitle)}
        stepName={intl.formatMessage(messages.selectFlowTitle)}
        subTitle={intl.formatMessage(messages.selectFlowSubTitle)}
      >
        <SelectFlow wizardState={wizardState} dispatchWizardState={dispatchWizardState!} />
      </WizardPage>

      <WizardPage
        isHidden={isAppCertFlow}
        isInputValid={wizardState.principalAccountName?.trim() && consentCheck.state === "granted"}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(messages.createApplicationTitle)}
        stepName={intl.formatMessage(messages.createApplicationTitle)}
        subTitle={""}
      >
        <CreateApplication
          wizardState={wizardState}
          dispatchWizardState={dispatchWizardState!}
          consentState={consentCheck.state}
          isChecking={consentCheck.isChecking}
          principalLimitError={principalLimitError}
          grantConsent={() => {
            grantUrl && grantConsentModern(grantUrl!, exchangeAuthCode);
          }}
        />
      </WizardPage>

      <WizardPage
        isHidden={!isAppCertFlow}
        isInputValid={
          !!wizardState.applicationId?.trim() &&
          !!wizardState.tenantId?.trim() &&
          !!wizardState.certificate
        }
        onNext={() => {
          dispatchWizardState({
            type: "update",
            payload: { subscriptionLoadTrigger: stateRef.current.subscriptionLoadTrigger + 1 },
          });
          return Promise.resolve();
        }}
        title={intl.formatMessage(messages.appCertFormTitle)}
        stepName={intl.formatMessage(messages.appCertFormStepName)}
        subTitle={intl.formatMessage(messages.appCertFormSubTitle)}
      >
        <AppCertForm wizardState={wizardState} dispatchWizardState={dispatchWizardState!} />
      </WizardPage>

      <WizardPage
        title={intl.formatMessage(messages.selectAzureResourceTitle)}
        stepName={intl.formatMessage(messages.selectAzureResourceTitle)}
        subTitle={intl.formatMessage(messages.selectAzureResourceSubTitle)}
        isInputValid={wizardState.subscription.subscriptionId}
        onNext={() => {
          if (stateRef.current.selectedFlow === "applicationCertificate") {
            const data = {
              subscriptionId: stateRef.current.subscription.subscriptionId,
              appId: stateRef.current.applicationId,
              tenantId: stateRef.current.tenantId,
              certificate: stateRef.current.certificate,
              ...(stateRef.current.certificatePassword
                ? { certificatePassword: stateRef.current.certificatePassword }
                : {}),
            };

            return addAppAzureSubscription(
              data,
              (subscription) => {
                setIsFinished(true);
                dispatchWizardState({
                  type: "update",
                  payload: { subscription: subscription },
                });
              },
              (error) => {
                dispatchWizardState({
                  type: "update",
                  payload: { createError: error },
                });
              }
            );
          }

          const data = {
            subscriptionId: stateRef.current.subscription.subscriptionId,
            accountName: stateRef.current.configurationAccount,
            accountToken: stateRef.current.refreshToken,

            createServicePrincipalAccount: true,
            dedicatedPrincipal: true,
            servicePrincipalName: stateRef.current.principalAccountName,
          };

          return addAzureSubscription(
            data,
            (subscription) => {
              setIsFinished(true);
              dispatchWizardState({
                type: "update",
                payload: { subscription: subscription },
              });
            },
            (error) => {
              dispatchWizardState({
                type: "update",
                payload: { createError: error },
              });
            }
          );
        }}
      >
        <SelectSubscription wizardState={wizardState} dispatchWizardState={dispatchWizardState!} />
      </WizardPage>

      <WizardPage
        title={intl.formatMessage(messages.addedSubscriptionStep)}
        stepName={intl.formatMessage(messages.addedSubscriptionStep)}
        subTitle={intl.formatMessage(messages.addedSubscriptionSubTitle)}
        isInputValid={true}
        onNext={() => Promise.resolve()}
      >
        <Finish wizardState={wizardState} />
      </WizardPage>
    </Wizard>
  );
};
